import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, GlobeIcon, CheckCircleIcon, StarIcon, PenToolIcon, CalendarIcon, HeartIcon, MailIcon, ExternalLinkIcon, ShareIcon, StoreIcon } from 'lucide-react';
import { getBookstoreBySlug, getBooksForBookstore } from '../data/bookstoresData';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useBrand } from '../hooks/useBrand';
const BookstoreProfile = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const dk = useDarkClasses();
  const {
    theme
  } = useBrand();
  const accentIcon = theme.accent.icon;
  const accentLink = theme.accent.link;
  const bookstore = slug ? getBookstoreBySlug(slug) : undefined;
  const linkedBooks = bookstore ? getBooksForBookstore(bookstore.id) : [];
  if (!bookstore) {
    return <div className={`min-h-screen flex items-center justify-center ${dk.page}`} data-id="element-7889" data-mp="3dqd97qr192qk">
        <div className="text-center" data-id="element-7890" data-mp="20tghzt6bp1a2">
          <StoreIcon className={`h-16 w-16 mx-auto mb-4 ${dk.textMuted}`} data-id="element-7891" data-mp="20uhttzv5xrsu" />
          <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-7892" data-mp="1w0wvu7tkkxtn">
            Bookstore Not Found
          </h2>
          <p className={`mb-6 ${dk.textSecondary}`} data-id="element-7893" data-mp="1r2va1f4rekeh">
            The bookstore you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/bookstores" className={`px-6 py-2 rounded-lg font-medium transition-colors ${theme.accent.button}`} data-id="element-7894" data-mp="2jrsqrdtbo954">
            Back to Directory
          </Link>
        </div>
      </div>;
  }
  const formatType = (type: string) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const verifiedColor = theme.accent.verified;
  const accentColor = theme.accent.button;
  const getAvailabilityBadge = (type: string) => {
    switch (type) {
      case 'stocked':
        return {
          label: 'In Stock',
          color: 'bg-green-500'
        };
      case 'online-listing':
        return {
          label: 'Online',
          color: 'bg-blue-500'
        };
      case 'signed-copy':
        return {
          label: 'Signed',
          color: 'bg-purple-500'
        };
      case 'pre-order':
        return {
          label: 'Pre-order',
          color: 'bg-amber-500'
        };
      default:
        return {
          label: 'Available',
          color: 'bg-gray-500'
        };
    }
  };
  return <div className={`min-h-screen pb-16 ${dk.page}`} data-id="element-7895" data-mp="hvbjtl5ghkqq">
      {/* Banner */}
      <div className="w-full h-48 md:h-64 lg:h-80 relative bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-900" data-id="element-7896" data-mp="35vv0aphjpdo">
        {bookstore.bannerImage && <img src={bookstore.bannerImage} alt="Banner" className="w-full h-full object-cover" data-id="element-7897" data-mp="3q90map86cgts" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-7898" data-mp="3v7u0ykz76dvj"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 md:-mt-24 relative z-10" data-id="element-7899" data-mp="3boxviqcjup77">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-7900" data-mp="36pl926plbk55">
          {/* Main Content */}
          <div className="flex-1" data-id="element-7901" data-mp="3kp32izdtzlku">
            {/* Header Info Card */}
            <div className={`rounded-2xl shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-7902" data-mp="3gkvcmyvmzzl1">
              <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center" data-id="element-7903" data-mp="3ul62pzp4ha55">
                <img src={bookstore.logo} alt={bookstore.name} className={`w-24 h-24 sm:w-32 sm:h-32 rounded-xl object-cover border-4 shadow-sm ${dk.isDark ? 'border-gray-800' : 'border-white'}`} data-id="element-7904" data-mp="3pmwbg06iqtot" />
                <div className="flex-1" data-id="element-7905" data-mp="394p54bxmu3v">
                  <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-7906" data-mp="h61fl4o529k8">
                    <h1 className={`text-2xl sm:text-3xl font-bold ${dk.textHeading}`} data-id="element-7907" data-mp="c7q8h1p8ad5p">
                      {bookstore.name}
                    </h1>
                    {bookstore.verified && <CheckCircleIcon className={`h-6 w-6 ${verifiedColor}`} title="Verified Store" data-id="element-7908" data-mp="2blqeeunqgkd6" />}
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-7909" data-mp="2kea61b5lxooi">
                      {formatType(bookstore.type)}
                    </span>
                  </div>

                  <div className={`flex flex-wrap items-center gap-4 text-sm mb-2 ${dk.textSecondary}`} data-id="element-7910" data-mp="1wofs0q9jxu2o">
                    <div className="flex items-center" data-id="element-7911" data-mp="38561ci99v20i">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-7912" data-mp="384o1lbnei5nu" />
                      {bookstore.city ? `${bookstore.city}, ` : ''}
                      {bookstore.country}
                    </div>
                    <div className="flex items-center text-yellow-500" data-id="element-7913" data-mp="3n75v2afjetvq">
                      <StarIcon className="h-4 w-4 fill-current mr-1" data-id="element-7914" data-mp="3i9w1qjp11y2m" />
                      <span className="font-bold" data-id="element-7915" data-mp="3dhmcl5vql21e">{bookstore.rating}</span>
                      <span className={`ml-1 ${dk.textMuted}`} data-id="element-7916" data-mp="3dfrrnrkgt9xb">
                        ({bookstore.reviewCount} reviews)
                      </span>
                    </div>
                  </div>

                  {bookstore.address && <p className={`text-sm mb-4 ${dk.textMuted}`} data-id="element-7917" data-mp="2zhbk5yes3fe4">
                      {bookstore.address}
                    </p>}

                  <div className="flex flex-wrap gap-2" data-id="element-7918" data-mp="2ul38ww3pc6c8">
                    {bookstore.specializations.map((spec, idx) => <span key={idx} className={`text-xs px-3 py-1 rounded-md font-medium ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`} data-id="element-7919" data-mp="ssf2kdvrh2s8" data-mpi={idx}>
                        {spec}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Description & Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-7920" data-mp="2969hsar7dvhc">
              <div className="md:col-span-2" data-id="element-7921" data-mp="2dar1k9xkc8s4">
                <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-7922" data-mp="30zil6cx37ita">
                  About the Store
                </h2>
                <p className={`whitespace-pre-line leading-relaxed ${dk.textSecondary}`} data-id="element-7923" data-mp="35yv970zp2vxg">
                  {bookstore.description}
                </p>
              </div>

              <div data-id="element-7924" data-mp="2wwvrs7krpptz">
                <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-7925" data-mp="2wwdlsj8azx8v">
                  Features
                </h2>
                <ul className="space-y-3" data-id="element-7926" data-mp="1gm1iiga79a4k">
                  {bookstore.features.signedCopies && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-7927" data-mp="1lgf7o0y9ql0s">
                      <PenToolIcon className={`h-5 w-5 mr-3 ${accentIcon}`} data-id="element-7928" data-mp="1czy2cdxyfxs3" />
                      Carries Signed Copies
                    </li>}
                  {bookstore.features.hostsEvents && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-7929" data-mp="1i1vszt8opuv2">
                      <CalendarIcon className={`h-5 w-5 mr-3 ${accentIcon}`} data-id="element-7930" data-mp="ub03j2rpbbqz" />
                      Hosts Author Events
                    </li>}
                  {bookstore.features.indieFocused && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-7931" data-mp="1435woftovz7r">
                      <HeartIcon className={`h-5 w-5 mr-3 ${accentIcon}`} data-id="element-7932" data-mp="z8rxe2bwp7xt" />
                      Indie-Focused Selection
                    </li>}
                  {bookstore.features.internationalShipping && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-7933" data-mp="3h8thecemermi">
                      <GlobeIcon className={`h-5 w-5 mr-3 ${accentIcon}`} data-id="element-7934" data-mp="3m6lavauwylst" />
                      International Shipping
                    </li>}
                </ul>
              </div>
            </div>

            {/* Linked Books Section */}
            <div data-id="element-7935" data-mp="41rmso80l6ly">
              <div className="flex items-center justify-between mb-6" data-id="element-7936" data-mp="406ruxhsflnk">
                <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-7937" data-mp="8zt7tep03g02">
                  Books Available Here
                </h2>
                <span className={`text-sm font-medium ${dk.textMuted}`} data-id="element-7938" data-mp="dscju781i32r">
                  {linkedBooks.length} titles
                </span>
              </div>

              {linkedBooks.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-7939" data-mp="nqse2j2d5sgh">
                  {linkedBooks.map((item, idx) => {
                const isSigned = item.linkInfo.availabilityType === 'signed-copy';
                const badge = getAvailabilityBadge(item.linkInfo.availabilityType);
                return <div key={idx} className="relative group" data-id="element-7940" data-mp="23a9ikfwu5lgf" data-mpi={idx}>
                        <UnifiedBookCard book={item.book} isSigned={isSigned} data-id="element-7941" data-mp="1yadob8dxx8e2" />
                        {!isSigned && <div className={`absolute bottom-2 right-2 z-30 ${badge.color} text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm`} data-id="element-7942" data-mp="2vw17usparw86">
                            {badge.label}
                          </div>}
                      </div>;
              })}
                </div> : <div className={`text-center py-12 rounded-xl border ${dk.card}`} data-id="element-7943" data-mp="3ep56ytlab1a6">
                  <BookIcon className={`h-10 w-10 mx-auto mb-3 ${dk.textMuted}`} data-id="element-7944" data-mp="bp6z0it9w3sc" />
                  <p className={dk.textSecondary} data-id="element-7945" data-mp="6r5j26r58kl3">
                    No books have been linked to this store yet.
                  </p>
                </div>}
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-6" data-id="element-7946" data-mp="gmytnklay408">
            {/* Action Card */}
            <div className={`rounded-xl shadow-sm p-6 border ${dk.card}`} data-id="element-7947" data-mp="umq8fnjn7ofa">
              <a href={bookstore.websiteUrl} target="_blank" rel="noopener noreferrer" className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors mb-3 ${accentColor}`} data-id="element-7948" data-mp="po6qrzc30ysq">
                <ExternalLinkIcon className="h-4 w-4 mr-2" data-id="element-7949" data-mp="1dfu8prrd8ziv" />
                Visit Website
              </a>

              <button className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors border ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`} data-id="element-7950" data-mp="19eiflc9a9w5x">
                <ShareIcon className="h-4 w-4 mr-2" data-id="element-7951" data-mp="1j1mc8sl8vgub" />
                Share Profile
              </button>
            </div>

            {/* Details Card */}
            <div className={`rounded-xl shadow-sm p-6 border ${dk.card}`} data-id="element-7952" data-mp="1o1qvgqdjepo8">
              <h3 className={`font-bold mb-4 ${dk.textHeading}`} data-id="element-7953" data-mp="199gknjywn1fn">
                Store Details
              </h3>

              <div className="space-y-4" data-id="element-7954" data-mp="1e3t9yan2b6zb">
                {/* Address Section */}
                <div data-id="element-7955" data-mp="10c6c46dh1qef">
                  <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-1`} data-id="element-7956" data-mp="108iil78113zp">
                    Address
                  </span>
                  {bookstore.type === 'online-only' ? <p className={`text-sm ${dk.textSecondary}`} data-id="element-7957" data-mp="v9xh74h1ge9o">
                      Online Only — No Physical Location
                    </p> : bookstore.address ? <p className={`text-sm ${dk.textPrimary}`} data-id="element-7958" data-mp="2s8mvu20clejc">
                      {bookstore.address}
                    </p> : <p className={`text-sm ${dk.textSecondary}`} data-id="element-7959" data-mp="2j8zvljjcuerv">
                      {bookstore.city ? `${bookstore.city}, ` : ''}
                      {bookstore.country}
                    </p>}
                </div>

                {bookstore.contactEmail && <div data-id="element-7960" data-mp="3bvudc8osf01n">
                    <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-1`} data-id="element-7961" data-mp="36yc2gcr7mar0">
                      Contact
                    </span>
                    <a href={`mailto:${bookstore.contactEmail}`} className={`flex items-center text-sm hover:underline ${accentLink}`} data-id="element-7962" data-mp="1vjyktpn1urjf">
                      <MailIcon className="h-4 w-4 mr-2" data-id="element-7963" data-mp="1gh6ssevsynu8" />
                      {bookstore.contactEmail}
                    </a>
                  </div>}

                <div data-id="element-7964" data-mp="1qel8uddtdam0">
                  <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-1`} data-id="element-7965" data-mp="1qe1pqtcfgg6e">
                    Ships To
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-7966" data-mp="1z7ev4uroha9d">
                    {bookstore.shippingRegions.map((region, idx) => <span key={idx} className={`text-xs px-2 py-1 rounded ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-7967" data-mp="244ey2wt8dww9" data-mpi={idx}>
                        {region}
                      </span>)}
                  </div>
                </div>

                {bookstore.socialLinks && bookstore.socialLinks.length > 0 && <div data-id="element-7968" data-mp="jrzhdzowdv41">
                    <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-2`} data-id="element-7969" data-mp="2a6qnvtirbjbu">
                      Social Media
                    </span>
                    <div className="flex flex-col gap-2" data-id="element-7970" data-mp="2j86k8xcqlkx2">
                      {bookstore.socialLinks.map((social, idx) => <a key={idx} href={social.url} target="_blank" rel="noopener noreferrer" className={`text-sm hover:underline ${accentLink}`} data-id="element-7971" data-mp="2e9dbfi8eai38" data-mpi={idx}>
                          {social.platform}
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Claim Profile */}
            {!bookstore.claimed && <div className={`rounded-xl p-5 border border-dashed ${dk.isDark ? 'border-gray-600 bg-gray-800/50' : 'border-gray-300 bg-gray-50'}`} data-id="element-7972" data-mp="12snb2fqnkkhj">
                <h4 className={`font-bold text-sm mb-2 ${dk.textPrimary}`} data-id="element-7973" data-mp="3j8a6wndw48dm">
                  Is this your store?
                </h4>
                <p className={`text-xs mb-3 ${dk.textSecondary}`} data-id="element-7974" data-mp="35hwx0fhfujln">
                  Claim this profile to manage your information, verify your
                  store, and approve book links.
                </p>
                <button className={`text-xs font-medium px-3 py-1.5 rounded border transition-colors ${dk.isDark ? 'border-gray-500 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-white text-gray-700'}`} data-id="element-7975" data-mp="20enm3m0p4j4">
                  Claim Profile
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default BookstoreProfile;